import {toast, dismissToast, retryButton} from '@lib/components/ui/toast';
import type {ReactNode} from 'react';
import {useCallback, useEffect} from 'react';

import {failedTasksSelector, pendingTasksSelector, successfulTasksSelector} from './selectors';
import type {BackgroundTasksStore, Task} from './types';
import {useBackgroundTasksStore} from './useBackgroundTasks';

/**
 * A hook that subscribes to background tasks store and shows UI elements to notify the user about the tasks status.
 */
export const useBackgroundTasksNotifications = () => {
  const updateTask = useBackgroundTasksStore(state => state.updateTask);
  const failedTasks = useBackgroundTasksStore(failedTasksSelector, areEqual);
  const pendingTasks = useBackgroundTasksStore(pendingTasksSelector, areEqual);
  const successTasks = useBackgroundTasksStore(successfulTasksSelector, areEqual);

  // Helper function to handle task notifications
  const handleTaskNotifications = useCallback(
    (
      tasks: Map<string, Task>,
      status: 'error' | 'loading' | 'success',
      toastIdPrefix: string,
      messageExtractor: (msg: any, taskId: string) => string,
      extraCondition: (task: Task) => boolean = () => true,
    ) => {
      tasks.forEach((task, taskId) => {
        const {type, isNotified, isFetching, toast: {id, ...msg} = {}} = task;
        // Use provided toast id or generate one based on the task id
        const toastId = `${toastIdPrefix}-${id ?? taskId}`;

        // Show notification if the task is not yet notified and meets extra conditions
        if (!isNotified && !task.silent && extraCondition(task)) {
          const retryResult = typeof task.toast?.retry === `function` ? task.toast.retry(status) : task.toast?.retry;

          let footer: ReactNode | {label: string; link: string} | undefined;
          if (retryResult?.onClick) {
            const onClick = retryResult.onClick;
            footer = retryButton(() => {
              dismissToast(toastId);
              onClick();
            });
          } else if (retryResult?.href) {
            footer = {label: retryResult.children ?? ``, link: String(retryResult.href)};
          }

          toast({
            id: toastId,
            title: messageExtractor(msg, taskId),
            type: status === `loading` ? `info` : status,
            isLoading: status === `loading`,
            footer,
          });
          updateTask(taskId, {type, isNotified: true});
        } else if (status !== `loading` && !isFetching) {
          // Close the pending task notification if it is active
          dismissToast(`task-pending-${id ?? taskId}`);
        }
      });
    },
    [updateTask],
  );

  // Effect to handle failed tasks notifications
  useEffect(() => {
    handleTaskNotifications(
      failedTasks,
      `error`,
      `task-failed`,
      (msg, taskId) => msg?.error || `Task ${taskId} failed`,
    );
  }, [updateTask, failedTasks, handleTaskNotifications]);

  // Effect to handle pending tasks notifications
  useEffect(() => {
    handleTaskNotifications(
      pendingTasks,
      `loading`,
      `task-pending`,
      (msg, taskId) => msg?.loading || `Task ${taskId} is being processed`,
      task => !!task.isFetching,
    );
  }, [updateTask, pendingTasks, handleTaskNotifications]);

  // Effect to handle successful tasks notifications
  useEffect(() => {
    handleTaskNotifications(
      successTasks,
      `success`,
      `task-success`,
      (msg, taskId) => msg?.success || `Task ${taskId} completed successfully`,
    );
  }, [updateTask, successTasks, handleTaskNotifications]);
};

const areEqual = (state: BackgroundTasksStore['tasks'], nextState: BackgroundTasksStore['tasks']): boolean => {
  if (state.size !== nextState.size) {
    return false;
  }

  for (const [taskId, task] of state) {
    const nextTask = nextState.get(taskId);

    if (!nextTask || nextTask.isNotified !== task.isNotified) {
      return false;
    }
  }

  return true;
};
